import { normalizeApiError } from '../api/apiError';
import { createReadAsyncThunk } from "../store/createReadAsyncThunk";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PcsEvent } from '../../features/pcsEventApproval/api/pcsEventApi';
import { ApiRequestStatus } from "../enums/apiRequestStatus";
import { PcsEventDto } from "../models/events/pcsEventDto";
import { FileDto } from "../models/membership/fileDto";
import { GetPcsEventDocumentParamObj } from "../../features/pcsEventApproval/common/getPcsEventDocumentParam";
import { PcsEventInstanceDto } from "../models/events/pcsEventInstanceDto";

interface PcsState {
    pcsEvents: PcsEventDto[] | null;
    pcsEventDetails: PcsEventDto | null;
    pcsEventId: string | null;
    pcsEventInstances: PcsEventInstanceDto[] | null;
    pcsEventDocuments: FileDto[] | null;
    isPcsEventDocumentSaved: boolean | null;
    isPcsEventDocumentDeleted: boolean | null;

    getPcsEventsStatus: ApiRequestStatus;
    getPcsEventStatus: ApiRequestStatus;
    savePcsEventStatus: ApiRequestStatus;
    updatePcsEventStatus: ApiRequestStatus;
    submitPcsEventStatus: ApiRequestStatus;
    getPcsEventInstancesStatus: ApiRequestStatus;
    getPcsEventDocumentsStatus: ApiRequestStatus;
    getPcsEventDocumentStatus: ApiRequestStatus;
    savePcsEventDocumentStatus: ApiRequestStatus;
    deletePcsEventDocumentStatus: ApiRequestStatus;
}

const initialState: PcsState = {
    pcsEvents: null,
    pcsEventDetails: null,
    pcsEventId: null,
    pcsEventInstances: null,
    pcsEventDocuments: null,
    isPcsEventDocumentSaved: null,
    isPcsEventDocumentDeleted: null,

    getPcsEventsStatus: ApiRequestStatus.Idle,
    getPcsEventStatus: ApiRequestStatus.Idle,
    savePcsEventStatus: ApiRequestStatus.Idle,
    updatePcsEventStatus: ApiRequestStatus.Idle,
    submitPcsEventStatus: ApiRequestStatus.Idle,
    getPcsEventInstancesStatus: ApiRequestStatus.Idle,
    getPcsEventDocumentsStatus: ApiRequestStatus.Idle,
    getPcsEventDocumentStatus: ApiRequestStatus.Idle,
    savePcsEventDocumentStatus: ApiRequestStatus.Idle,
    deletePcsEventDocumentStatus: ApiRequestStatus.Idle
}

export const getPcsEventsAsync = createReadAsyncThunk<PcsEventDto[]>(
    'pcsEventSlice/getPcsEventsAsync',
    async (_, thunkAPI) => {
        try {
            return await PcsEvent.gePcsEventsAsync();
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const gePcsEventByIdAsync = createReadAsyncThunk<PcsEventDto, string>(
    'pcsEventSlice/gePcsEventByIdAsync',
    async (eventId, thunkAPI) => {
        try {
            return await PcsEvent.gePcsEventByIdAsync(eventId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const savePcsEventAsync = createAsyncThunk<string, PcsEventDto>(
    'pcsEventSlice/savePcsEventAsync',
    async (pcsEventDetails, thunkAPI) => {
        try {
            return await PcsEvent.savePcsEventAsync(pcsEventDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const updatePcsEventAsync = createAsyncThunk<void, PcsEventDto>(
    'pcsEventSlice/updatePcsEventAsync',
    async (pcsEventDetails, thunkAPI) => {
        try {
            return await PcsEvent.updatePcsEventAsync(pcsEventDetails);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const submitPcsEventAsync = createAsyncThunk<void, string>(
    'pcsEventSlice/submitPcsEventAsync',
    async (eventId, thunkAPI) => {
        try {
            return await PcsEvent.submitPcsEventAsync(eventId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getPcsEventInstancesAsync = createReadAsyncThunk<PcsEventInstanceDto[], string>(
    'pcsEventSlice/getPcsEventInstancesAsync',
    async (eventId, thunkAPI) => {
        try {
            return await PcsEvent.getPcsEventInstancesAsync(eventId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getPcsEventDocumentsAsync = createReadAsyncThunk<FileDto[], string>(
    'pcsEventSlice/getPcsEventDocumentsAsync',
    async (eventId, thunkAPI) => {
        try {
            return await PcsEvent.getPcsEventDocumentsAsync(eventId);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const getPcsEventDocumentAsync = createReadAsyncThunk<Blob, GetPcsEventDocumentParamObj>(
    'pcsEventSlice/getPcsEventDocumentAsync',
    async (getPcsEventDocumentParamObj, thunkAPI) => {
        try {
            return await PcsEvent.getPcsEventDocumentAsync(getPcsEventDocumentParamObj);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const savePcsEventDocumentAsync = createAsyncThunk<boolean, FormData>(
    'pcsEventSlice/savePcsEventDocumentAsync',
    async (formData, thunkAPI) => {
        try {
            return await PcsEvent.savePcsEventDocumentAsync(formData);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const deletePcsEventDocumentAsync = createAsyncThunk<boolean, GetPcsEventDocumentParamObj>(
    'pcsEventSlice/deletePcsEventDocumentAsync',
    async (getPcsEventDocumentParamObj, thunkAPI) => {
        try {
            return await PcsEvent.deleteDocumentAsync(getPcsEventDocumentParamObj);
        } catch (error: unknown) {
            return thunkAPI.rejectWithValue(normalizeApiError(error));
        }
    }
)

export const pcsEventSlice = createSlice({
    name: 'pcsEventSlice',
    initialState,
    reducers: {

    },
    extraReducers: (builder => {
        builder.addCase(getPcsEventsAsync.pending, (state) => {
            state.getPcsEventsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEventsAsync.fulfilled, (state, action) => {
            state.pcsEvents = action.payload;
            state.getPcsEventsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEventsAsync.rejected, (state) => {
            state.getPcsEventsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(gePcsEventByIdAsync.pending, (state) => {
            state.getPcsEventStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(gePcsEventByIdAsync.fulfilled, (state, action) => {
            state.pcsEventDetails = action.payload;
            state.getPcsEventStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(gePcsEventByIdAsync.rejected, (state) => {
            state.getPcsEventStatus = ApiRequestStatus.Rejected;
        });
        
        builder.addCase(savePcsEventAsync.pending, (state) => {
            state.savePcsEventStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(savePcsEventAsync.fulfilled, (state, action) => {
            state.pcsEventId = action.payload;
            state.savePcsEventStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(savePcsEventAsync.rejected, (state) => {
            state.savePcsEventStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(updatePcsEventAsync.pending, (state) => {
            state.updatePcsEventStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(updatePcsEventAsync.fulfilled, (state) => {
            state.updatePcsEventStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(updatePcsEventAsync.rejected, (state) => {
            state.updatePcsEventStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(submitPcsEventAsync.pending, (state) => {
            state.submitPcsEventStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(submitPcsEventAsync.fulfilled, (state) => {
            state.submitPcsEventStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(submitPcsEventAsync.rejected, (state) => {
            state.submitPcsEventStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEventInstancesAsync.pending, (state) => {
            state.getPcsEventInstancesStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEventInstancesAsync.fulfilled, (state, action) => {
            state.pcsEventInstances = action.payload;
            state.getPcsEventInstancesStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEventInstancesAsync.rejected, (state) => {
            state.getPcsEventInstancesStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEventDocumentsAsync.pending, (state) => {
            state.getPcsEventDocumentsStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEventDocumentsAsync.fulfilled, (state, action) => {
            state.pcsEventDocuments = action.payload;
            state.getPcsEventDocumentsStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEventDocumentsAsync.rejected, (state) => {
            state.getPcsEventDocumentsStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(getPcsEventDocumentAsync.pending, (state) => {
            state.getPcsEventDocumentStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(getPcsEventDocumentAsync.fulfilled, (state) => {
            state.getPcsEventDocumentStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(getPcsEventDocumentAsync.rejected, (state) => {
            state.getPcsEventDocumentStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(savePcsEventDocumentAsync.pending, (state) => {
            state.savePcsEventDocumentStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(savePcsEventDocumentAsync.fulfilled, (state, action) => {
            state.isPcsEventDocumentSaved = action.payload;
            state.savePcsEventDocumentStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(savePcsEventDocumentAsync.rejected, (state) => {
            state.savePcsEventDocumentStatus = ApiRequestStatus.Rejected;
        });

        builder.addCase(deletePcsEventDocumentAsync.pending, (state) => {
            state.deletePcsEventDocumentStatus = ApiRequestStatus.Pending;
        });
        builder.addCase(deletePcsEventDocumentAsync.fulfilled, (state, action) => {
            state.isPcsEventDocumentDeleted = action.payload;
            state.deletePcsEventDocumentStatus = ApiRequestStatus.Fulfilled;
        });
        builder.addCase(deletePcsEventDocumentAsync.rejected, (state) => {
            state.deletePcsEventDocumentStatus = ApiRequestStatus.Rejected;
        });
    })
})
